// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.VirtualFormFrontSide.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

const DEFAULT_SCROLL_THROTTLE = 150;
// $FlowFixMe
import { debounce, forEach, isEmpty, get, isNil, concat, has, find, remove, isEqual, head, flow } from 'lodash/fp';
import { toJS } from 'mobx';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { propTypes as MobxPropTypes } from 'mobx-react';

import AppContext from 'share/enhancers/Izenda.AppContext';
import connect from 'share/enhancers/Izenda.Connect';
import elementResizeDetector from 'share/utils/Izenda.SizeDetector';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import Utils from 'share/utils/Izenda.DomUtils';
import VirtualFormFrontSideStore from 'logic/Izenda.VirtualFormFrontSideStore';

import EmbeddedReport from './Izenda.EmbeddedReport';

const NUMBER_ZERO = 0;
const NUMBER_ONE = 1;
const NUMBER_THREE = 3;
@connect()
export default class ReportPartFormBackSide extends Component {
  static propTypes = {
    embeddedReports: MobxPropTypes.arrayOrObservableArray.isRequired,
    embeddedSubReports: MobxPropTypes.arrayOrObservableArray.isRequired,
    finishGetData: PropTypes.bool,
    viewHeight: PropTypes.number,
    printingMode: PropTypes.bool
  };

  store: VirtualFormFrontSideStore;
  needUnmount: Array<any> = [];
  renderListeners: Array<string> = [];
  elementResizeListener: Array<string> = [];
  virtualForm: any = null;
  reportDom: any = null;

  constructor(props: any) {
    super(props);
    this.store = new VirtualFormFrontSideStore(props.viewHeight);
  }

  componentDidMount() {
    const scrollParent = Utils.findScrollableParent(this.virtualForm);
    const throttleScroll = debounce({ maxWait: DEFAULT_SCROLL_THROTTLE * NUMBER_THREE }, this.onScroll);
    scrollParent &&
      scrollParent.addEventListener('scroll', throttleScroll, {
        capture: false,
        passive: true
      });
    this.props.embededReportPart && this.publishFinishedRender();
  }

  componentDidUpdate(prevProps) {
    const {
      embeddedReports: prevEmbeddedReports,
      embeddedSubReports: prevEmbeddedSubReports
    } = prevProps;
    const {
      embeddedReports,
      embeddedSubReports
    } = this.props;
    if (
      !isEqual(toJS(prevEmbeddedReports), toJS(embeddedReports)) ||
      !isEqual(toJS(prevEmbeddedSubReports), toJS(embeddedSubReports))
    ) {
      this.store.cleanUp();
      this.store.updateItems(this.getData());
      this.renderReports(NUMBER_ZERO);
      this.publishFinishedRender();
    }
  }

  componentWillUnmount() {
    forEach(ReactDOM.unmountComponentAtNode.bind(ReactDOM), this.needUnmount);
    forEach(listener => {
      elementResizeDetector.removeListener(listener.element, listener.listener);
    }, this.elementResizeListener);
    this.needUnmount = [];
    this.elementResizeListener = [];
  }

  /**
   * create context provider for ReportPartViewer
   * @param {Object} context
   */
  createContextProvider(context: any) {
    return React.createClass({
      childContextTypes: {
        loader: React.PropTypes.object,
        dialogs: React.PropTypes.object,
        exportLoader: React.PropTypes.object,
        validation: React.PropTypes.object,
        authorization: React.PropTypes.object,
        authorizationConstants: React.PropTypes.object,
        localization: React.PropTypes.object
      },
      getChildContext() {
        return {
          loader: context.loader,
          exportLoader: context.exportLoader,
          dialogs: context.dialogs,
          validation: context.validation,
          authorization: context.authorization,
          authorizationConstants: context.authorizationConstants,
          localization: context.localization
        };
      },
      render() {
        return this.props.children;
      }
    });
  }

  /**
   * SetDom
   * @param  {Object} dom
   */
  setDom = (dom: any) => {
    this.reportDom = dom;
  };

  onScroll = (scrollEvent: any) => {
    const store = this.store;
    const scrollTop = scrollEvent.target.scrollTop;
    const reports = store.getItemsOnViewScreen();
    const firstReport = head(reports);
    const firstReportId = get('reportPartDomId', firstReport);

    // only render the first report if it has not rendered
    if (store.hasVirtualSection(scrollTop) &&
      store.virtualSections.length === NUMBER_ONE &&
      !store.checkRenderedItem(firstReportId)) {
      this.renderReports(NUMBER_ZERO);
    }
  };

  /**
   * Get list of report part embedded
   * @return {Array}
   */
  getData = () => {
    const props = this.props;
    return props.finishGetData ?
      flow(
        toJS,
        concat(toJS(props.embeddedSubReports)),
      )(props.embeddedReports) : [];
  };

  /**
   * Call finish render
   */
  publishFinishedRender = () => {
    const props = this.props;

    PrintingUtils.publishFinishedRender(this.renderListeners, () => {
      props.finishedRender && props.finishedRender();
    });
  };

  /**
   * render embedded Reports
   * @param {Array} embeddedReports
   */
  renderReports = (startIndex: number) => {
    const store = this.store;
    const reports = store.getItemsOnViewScreen();
    const report = reports[startIndex];

    if (!isEmpty(reports) && startIndex < store.items.length && !isNil(report)) {
      // get Context for ReportPartViewer
      const ContextProvider = this.createContextProvider(AppContext.createAppContext());
      const renderListeners = this.renderListeners;
      const $masterDomNode = $izenda(this.props.masterDomNode);

      // get list of embedded reports via className
      const embededReport = $masterDomNode.find('.' + report.reportPartDomId).get(NUMBER_ZERO);
      if (!embededReport) {
        return;
      }

      const listenerId = startIndex + '.' + report.reportPartDomId;
      PrintingUtils.pushRenderListener(renderListeners, listenerId);
      const afterRenderCallBack = this.afterRenderCallBack.bind(null, embededReport);

      const isSubReport = has('fieldInfo', report);
      let restProps;
      if (isSubReport) {
        restProps = {
          reportPartDef: report.reportPartDef,
          subReportFilter: report.queryObject,
          fieldInfo: report.fieldInfo
        };
      } else {
        restProps = {
          reportPartId: report.id,
          containerWidth: report.width,
          containerHeight: report.height
        };
      }

      ReactDOM.render(
        <ContextProvider>
          <EmbeddedReport
            ref={this.setDom}
            notInvokeRenderCallBackAfterDidMount
            printingMode={this.props.printingMode}
            afterRenderCallBack={afterRenderCallBack}
            finishedRender={this.checkNextRendering.bind(this, report, startIndex, listenerId)}
            viewMode={this.props.viewMode}
            forDashboard={this.props.forDashboard}
            ignoreCache={this.props.ignoreCache}
            {...restProps}
          />
        </ContextProvider>,
        embededReport
      );

      this.needUnmount.push(embededReport);
    } else {
      store.stop();
    }
  };

  /**
   * Check next render report part
   * @param  {Object} report:                [description]
   * @param  {Number} index:      number        [description]
   * @param  {String} listenerId: string        [description]
   */
  checkNextRendering(report: any, index: number, listenerId: string) {
    const store = this.store;
    const reportPartDomId = report.reportPartDomId;
    if (!store.checkRenderedItem(reportPartDomId)) {
      store.updateRenderedIds(reportPartDomId);
      if (this.props.printingMode) {
        PrintingUtils.getFinishedRenderPromiseById(this.renderListeners, listenerId);
        this.renderReports(index + NUMBER_ONE);
      } else {
        const reportDOM = this.reportDom;
        const hasVacancy = store.hasVacancy(get('offsetHeight', reportDOM), report.reportPartDomId, index);
        if (hasVacancy) {
          this.renderReports(index + NUMBER_ONE);
        } else {
          store.updateVirtualSections();
          !isEmpty(store.virtualSections) && this.renderReports(NUMBER_ZERO);
        }
      }
    }
  }

  /**
   * set height for Embedded SubReport after finished rendering
   * @param {Object} domObject
   */
  afterRenderCallBack = (domObject: any) => {
    const props = this.props;
    const frontSideViewer = domObject.querySelector('.izenda-ReportPartFrontSideViewer-container');
    let listener = find({ id: domObject.id }, this.elementResizeListener);

    if (listener) {
      elementResizeDetector.removeListener(listener.element, listener.listener);
      this.elementResizeListener = remove(listener, this.elementResizeListener);
    }
    if (frontSideViewer) {
      listener = {
        element: frontSideViewer,
        listener: elementResizeDetector.listenTo(frontSideViewer, () => {
          // Only update table width when the container has been resized.
          domObject.style.height = props.printingMode ? '100%' : frontSideViewer.offsetHeight + 'px';
        })
      };
      this.elementResizeListener = concat([listener], this.elementResizeListener);
    }
  };

  render() {
    return <div ref={node => (this.virtualForm = node)}>{this.props.children}</div>;
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartForm/Izenda.VirtualFormFrontSide.js